import * as React from 'react';
import {connect} from 'react-redux';

// @ts-expect-error
import decapitalize from 'underscore.string/decapitalize';

import {safelyGetRelationshipFilterIds} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import type {NutshellState} from '../../store';

import * as analyticsSelectors from '../email-marketing/analytics/report/analytics-selectors';

import * as completedActionsSelectors from './engagement/completed-actions/completed-actions-selectors';
import * as engagementLeadsSelectors from './engagement/leads/leads-selectors';
import * as engagementSalesSelectors from './engagement/sales/sales-selectors';
import * as widgetOpensSelectors from './engagement/widget-opens/widget-opens-selectors';
import {
    GAP_SELECT_VALUES_DAY_TO_YEAR,
    FORECAST,
    ACTIVITY,
    EMAIL,
    TASKS,
    QUOTES,
    ENGAGEMENT_LEADS,
    ENGAGEMENT_SALES,
    WIDGET_OPENS,
    COMPLETED_ACTIONS,
    VISITORS,
} from './report-constants';
import type {
    CustomLeadStats,
    ForecastStats,
    ActivityStats,
    ReportSummaryKey,
    EmailStats,
    QuotesStats,
} from './report-summary';
import {ReportSummary} from './report-summary';
import type {ChartReportParams, ReportKey} from './report-types';
import * as reportSelectors from './reports-selectors';

type OwnProps = {
    currentGapIndex: number;
    filterParams: FilterObject;
    reportKey: ReportKey;
    reportParams: ChartReportParams;
};

type StateProps = {
    chartBucketCount: number;
    summaryStats: CustomLeadStats | ForecastStats | ActivityStats | EmailStats | QuotesStats;
    isSummaryStatsLoading: boolean;
};

type Props = OwnProps & StateProps;

function mapStateToProps(state: NutshellState, ownProps: OwnProps): StateProps {
    let chartBucketCount;
    let summaryStats;
    let isSummaryStatsLoading;

    switch (ownProps.reportKey) {
        case ENGAGEMENT_LEADS:
            chartBucketCount = engagementLeadsSelectors.getCurrentReportChartBucketCount(state);
            summaryStats = engagementLeadsSelectors.getSummaryStats(state);
            isSummaryStatsLoading = engagementLeadsSelectors.getSummaryStatsIsLoading(state);
            break;
        case ENGAGEMENT_SALES:
            chartBucketCount = engagementSalesSelectors.getCurrentReportChartBucketCount(state);
            summaryStats = engagementSalesSelectors.getSummaryStats(state);
            isSummaryStatsLoading = engagementSalesSelectors.getSummaryStatsIsLoading(state);
            break;
        case WIDGET_OPENS:
            chartBucketCount = widgetOpensSelectors.getChartBucketCount(state);
            summaryStats = widgetOpensSelectors.getSummaryStats(state);
            isSummaryStatsLoading = widgetOpensSelectors.getIsLoading(state);
            break;
        case COMPLETED_ACTIONS:
            chartBucketCount = completedActionsSelectors.getChartBucketCount(state);
            summaryStats = completedActionsSelectors.getSummaryStats(state);
            isSummaryStatsLoading = completedActionsSelectors.getIsLoading(state);
            break;
        case VISITORS:
            chartBucketCount = analyticsSelectors.getCurrentReportChartBucketWithValuesCount(state);
            summaryStats = analyticsSelectors.getSummaryStatsStats(state);
            isSummaryStatsLoading = analyticsSelectors.getSummaryStatsIsLoading(state);
            break;
        default:
            chartBucketCount = reportSelectors.getCurrentReportChartBucketCount(state);
            summaryStats = reportSelectors.getSummaryStatsStats(state);
            isSummaryStatsLoading = reportSelectors.getSummaryStatsIsLoading(state);
    }

    return {
        chartBucketCount,
        summaryStats,
        isSummaryStatsLoading,
    };
}
/*
 * This provides a set of rolled-up summary data for our reports.
 * For leads, we'll include information like total value, average time open,
 * average lead value, etc.
 *
 * For activities, we include information like total activities, total duration,
 * avg duration, etc.
 */
export function ReportDataSummaryComponent(props: Props) {
    const {
        chartBucketCount,
        currentGapIndex,
        filterParams,
        isSummaryStatsLoading,
        reportKey,
        reportParams,
        summaryStats,
    } = props;

    return (
        <ReportSummary
            summaryType={getSummaryType(reportKey, reportParams.useConfidence === 'true')}
            stats={summaryStats}
            facet={reportParams.facet}
            // @ts-expect-error
            gapLabel={decapitalize(GAP_SELECT_VALUES_DAY_TO_YEAR[currentGapIndex].label)}
            chartBucketCount={chartBucketCount}
            leadStatuses={getLeadStatuses(filterParams)}
            isLoading={isSummaryStatsLoading}
        />
    );
}

function getSummaryType(reportKey: ReportKey, useConfidence?: boolean): ReportSummaryKey {
    switch (reportKey) {
        case FORECAST:
            return useConfidence ? 'forecastWithConfidence' : 'forecast';
        case ACTIVITY:
            return 'activity';
        case EMAIL:
            return 'email';
        case TASKS:
            return 'tasks';
        case QUOTES:
            return 'quotes';
        case ENGAGEMENT_LEADS:
            return 'engagementLeads';
        case ENGAGEMENT_SALES:
            return 'engagementSales';
        case WIDGET_OPENS:
            return 'widgetOpens';
        case COMPLETED_ACTIONS:
            return 'completedActions';
        case VISITORS:
            return 'visitors';
        default:
            return 'customLead';
    }
}

// We recently moved to a different filter object schema, which is
// a different shape than the previous one. Using our helper method
// here because our status filter can be one of two types
// @ts-expect-error
function getLeadStatuses(filterParams) {
    return filterParams && filterParams.status
        ? safelyGetRelationshipFilterIds(filterParams.status)
        : [];
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps);
export const ReportDataSummary = connector(ReportDataSummaryComponent);
